import React, { useMemo, useState } from "react";
import { toast } from "react-toastify";

import {
  ASO_TYPE_LABELS,
  createEmployeeDocumentApi,
  DOCUMENT_CATEGORY_LABELS,
  DOCUMENT_FILE_MAX_BYTES,
  DOCUMENT_FILE_TYPES,
  documentTypeName,
  DpAsoType,
  DpDocumentType,
  DpEmployeeDocument,
  supersedeEmployeeDocumentApi,
  updateEmployeeDocumentApi,
  uploadDocumentFileApi,
} from "../api/documentsApi";
import type { DpEmployeeContract } from "../api/contractsApi";
import { formatDateOnly } from "../utils/format";

type Props = {
  token: string;
  employeeId: string;
  documentTypes: DpDocumentType[];
  contracts: DpEmployeeContract[];
  /** Substituição: o tipo fica fixo (se o anterior tinha) e o anterior passa a ser histórico. */
  superseding?: DpEmployeeDocument | null;
  /** Edição dos dados (tipo, datas, título, observação) — sem trocar o arquivo. */
  editing?: DpEmployeeDocument | null;
  /** Tipo pré-escolhido (ex.: etapa de checklist que exige um documento). */
  fixedTypeId?: string | null;
  onClose: () => void;
  /** No envio em lote, recebe o último documento salvo. */
  onSaved: (document: DpEmployeeDocument) => void;
};

/** 'YYYY-MM-DD' + N dias, sem passar pelo fuso local. */
const addDaysIso = (iso: string, days: number) => {
  const date = new Date(`${iso}T00:00:00.000Z`);
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
};

/** Título do documento no envio em lote: o nome do arquivo sem a extensão. */
const titleFromFileName = (name: string) => name.replace(/\.[^.]+$/, "").slice(0, 200);

export default function DocumentFormModal({
  token,
  employeeId,
  documentTypes,
  contracts,
  superseding,
  editing,
  fixedTypeId,
  onClose,
  onSaved,
}: Props) {
  const lockedTypeId = superseding?.documentTypeId ?? fixedTypeId ?? "";
  const allowsBatch = !superseding && !editing && !fixedTypeId;
  const [form, setForm] = useState({
    documentTypeId: editing?.documentTypeId ?? lockedTypeId,
    asoType: (editing?.asoType ?? "") as DpAsoType | "",
    title: editing?.title ?? "",
    issuedAt: editing?.issuedAt?.slice(0, 10) ?? "",
    expiresAt: editing?.expiresAt?.slice(0, 10) ?? "",
    contractId: superseding?.contractId ?? "",
    notes: editing?.notes ?? "",
  });
  const [files, setFiles] = useState<File[]>([]);
  const [saving, setSaving] = useState(false);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);

  const type = useMemo(
    () => documentTypes.find((t) => t.id === form.documentTypeId) ?? null,
    [documentTypes, form.documentTypeId]
  );
  const isAso = type?.category === "ASO";
  const isBatch = files.length > 1;

  const suggestedExpiry =
    type?.defaultValidityDays && form.issuedAt ? addDaysIso(form.issuedAt, type.defaultValidityDays) : null;

  const handleFiles = (list: FileList | null) => {
    const selected = Array.from(list ?? []);
    const invalidType = selected.filter((f) => !DOCUMENT_FILE_TYPES.includes(f.type));
    const tooBig = selected.filter((f) => f.size > DOCUMENT_FILE_MAX_BYTES);
    if (invalidType.length > 0) {
      toast.error(`Envie PDF, JPG, PNG ou WEBP. Fora do padrão: ${invalidType.map((f) => f.name).join(", ")}.`);
    }
    if (tooBig.length > 0) {
      toast.error(`O arquivo pode ter no máximo 10 MB. Acima: ${tooBig.map((f) => f.name).join(", ")}.`);
    }
    setFiles(selected.filter((f) => !invalidType.includes(f) && !tooBig.includes(f)));
  };

  const validate = () => {
    if (isAso && !form.asoType) return "Informe o tipo do ASO.";
    if (type?.requiresExpiry && !form.expiresAt && !suggestedExpiry) {
      return "Este tipo de documento exige data de validade.";
    }
    return null;
  };

  const saveEdit = async (document: DpEmployeeDocument) => {
    const saved = await updateEmployeeDocumentApi(token, document.id, {
      documentTypeId: form.documentTypeId || null,
      asoType: isAso ? (form.asoType as DpAsoType) : null,
      title: form.title.trim() || null,
      issuedAt: form.issuedAt || null,
      expiresAt: form.expiresAt || null,
      notes: form.notes.trim() || null,
    });
    toast.success("Documento salvo.");
    onSaved(saved);
  };

  /** Um documento por arquivo, na ordem. Os que falharem ficam no modal para tentar de novo. */
  const saveBatch = async () => {
    const failed: File[] = [];
    let last: DpEmployeeDocument | null = null;
    setProgress({ done: 0, total: files.length });
    for (let index = 0; index < files.length; index += 1) {
      const file = files[index];
      try {
        const uploaded = await uploadDocumentFileApi(token, file);
        last = await createEmployeeDocumentApi(token, employeeId, {
          ...commonPayload(),
          title: titleFromFileName(file.name),
          assetId: uploaded.assetId,
          fileName: uploaded.fileName ?? file.name,
        });
      } catch {
        failed.push(file);
      }
      setProgress({ done: index + 1, total: files.length });
    }
    setProgress(null);

    const sent = files.length - failed.length;
    if (failed.length === 0 && last) {
      toast.success(`${sent} documentos lançados.`);
      onSaved(last);
      return;
    }
    setFiles(failed);
    toast.error(
      `${sent} de ${files.length} enviados. Falharam: ${failed.map((f) => f.name).join(", ")}. Tente de novo.`
    );
  };

  const commonPayload = () => ({
    documentTypeId: form.documentTypeId || null,
    asoType: isAso ? (form.asoType as DpAsoType) : null,
    issuedAt: form.issuedAt || null,
    // Vazio = o backend calcula pela validade padrão do tipo.
    expiresAt: form.expiresAt || null,
    contractId: form.contractId || null,
    notes: form.notes.trim() || null,
  });

  const submit = async () => {
    const error = validate();
    if (error) return toast.error(error);

    setSaving(true);
    try {
      if (editing) return await saveEdit(editing);
      if (isBatch) return await saveBatch();

      const file = files[0] ?? null;
      const uploaded = file ? await uploadDocumentFileApi(token, file) : null;
      const payload = {
        ...commonPayload(),
        title: form.title.trim() || null,
        assetId: uploaded?.assetId ?? null,
        fileName: uploaded?.fileName ?? (file ? file.name : null),
      };

      const saved = superseding
        ? await supersedeEmployeeDocumentApi(token, superseding.id, payload)
        : await createEmployeeDocumentApi(token, employeeId, payload);

      toast.success(superseding ? "Documento substituído." : "Documento lançado.");
      onSaved(saved);
    } catch (error: any) {
      toast.error(error?.message || "Erro ao salvar o documento.");
    } finally {
      setSaving(false);
    }
  };

  const title = editing ? "Editar documento" : superseding ? "Substituir documento" : "Novo documento";

  return (
    <div className="dpModalOverlay">
      <div className="dpModal">
        <header className="dpModal__header">
          <h2 className="dpModal__title">{title}</h2>
          <button className="dpModal__close" onClick={onClose} disabled={saving}>
            &times;
          </button>
        </header>

        <div className="dpModal__body">
          {superseding ? (
            <p className="dpNotice">
              O documento atual ({documentTypeName(superseding)}
              {superseding.issuedAt ? `, emitido em ${formatDateOnly(superseding.issuedAt)}` : ""}) fica guardado no
              histórico, e este passa a ser o vigente.
            </p>
          ) : null}

          {isBatch ? (
            <p className="dpNotice">
              {files.length} arquivos: cada um vira um documento, com o nome do arquivo como título. Tipo, datas,
              contrato e observação preenchidos aqui valem para todos; o que ficar em branco dá para completar depois em
              "Editar", documento a documento.
            </p>
          ) : null}

          <div className="dpFormGrid">
            <div className="dpFormGroup">
              <label htmlFor="docType">Tipo de documento</label>
              <select
                id="docType"
                className="dpSelect"
                value={form.documentTypeId}
                disabled={saving || Boolean(lockedTypeId)}
                onChange={(e) => setForm((f) => ({ ...f, documentTypeId: e.target.value, asoType: "" }))}
              >
                <option value="">Sem tipo (definir depois)</option>
                {documentTypes.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name} ({DOCUMENT_CATEGORY_LABELS[t.category]})
                  </option>
                ))}
              </select>
            </div>

            {isAso ? (
              <div className="dpFormGroup">
                <label htmlFor="docAso">Tipo do ASO *</label>
                <select
                  id="docAso"
                  className="dpSelect"
                  value={form.asoType}
                  disabled={saving}
                  onChange={(e) => setForm((f) => ({ ...f, asoType: e.target.value as DpAsoType }))}
                >
                  <option value="">Selecione...</option>
                  {(Object.keys(ASO_TYPE_LABELS) as DpAsoType[]).map((a) => (
                    <option key={a} value={a}>
                      {ASO_TYPE_LABELS[a]}
                    </option>
                  ))}
                </select>
              </div>
            ) : null}

            <div className="dpFormGroup">
              <label htmlFor="docIssued">Emissão</label>
              <input
                id="docIssued"
                type="date"
                className="dpInput"
                value={form.issuedAt}
                disabled={saving}
                onChange={(e) => setForm((f) => ({ ...f, issuedAt: e.target.value }))}
              />
            </div>

            <div className="dpFormGroup">
              <label htmlFor="docExpires">Validade{type?.requiresExpiry ? " *" : ""}</label>
              <input
                id="docExpires"
                type="date"
                className="dpInput"
                value={form.expiresAt}
                disabled={saving}
                onChange={(e) => setForm((f) => ({ ...f, expiresAt: e.target.value }))}
              />
              {suggestedExpiry && !form.expiresAt ? (
                <span className="dpFormHint">
                  Vazio = {formatDateOnly(suggestedExpiry)} (validade padrão de {type?.defaultValidityDays} dias).
                </span>
              ) : null}
            </div>

            {!isBatch ? (
              <div className="dpFormGroup">
                <label htmlFor="docTitle">Título</label>
                <input
                  id="docTitle"
                  className="dpInput"
                  maxLength={200}
                  placeholder="Ex.: Aditivo 02/2026 — mudança de jornada"
                  value={form.title}
                  disabled={saving}
                  onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                />
              </div>
            ) : null}

            {!editing && contracts.length > 0 ? (
              <div className="dpFormGroup">
                <label htmlFor="docContract">Contrato</label>
                <select
                  id="docContract"
                  className="dpSelect"
                  value={form.contractId}
                  disabled={saving}
                  onChange={(e) => setForm((f) => ({ ...f, contractId: e.target.value }))}
                >
                  <option value="">Não vincular</option>
                  {contracts.map((c) => (
                    <option key={c.id} value={c.id}>
                      Admissão {formatDateOnly(c.admissionDate)}
                      {c.termination ? " (rescindido)" : ""}
                    </option>
                  ))}
                </select>
              </div>
            ) : null}
          </div>

          {!editing ? (
            <div className="dpFormGroup">
              <label htmlFor="docFile">
                {allowsBatch ? "Arquivos (PDF ou imagem, até 10 MB cada)" : "Arquivo (PDF ou imagem, até 10 MB)"}
              </label>
              <input
                id="docFile"
                type="file"
                className="dpInput"
                // No celular, `accept` já oferece câmera, galeria e arquivos.
                accept={DOCUMENT_FILE_TYPES.join(",")}
                multiple={allowsBatch}
                disabled={saving}
                onChange={(e) => handleFiles(e.target.files)}
              />
              {files.length === 1 ? (
                <span className="dpFormHint">
                  {files[0].name} · {(files[0].size / 1024 / 1024).toFixed(2)} MB
                </span>
              ) : files.length > 1 ? (
                <ul className="dpFormHint" style={{ margin: "4px 0 0", paddingLeft: 18 }}>
                  {files.map((f) => (
                    <li key={`${f.name}-${f.size}`}>
                      {f.name} · {(f.size / 1024 / 1024).toFixed(2)} MB
                    </li>
                  ))}
                </ul>
              ) : (
                <span className="dpFormHint">
                  {allowsBatch
                    ? "Dá para escolher vários arquivos de uma vez. Opcional: dá para registrar só as datas e anexar depois, por substituição."
                    : "Opcional: dá para registrar só as datas e anexar depois, por substituição."}
                </span>
              )}
            </div>
          ) : null}

          <div className="dpFormGroup">
            <label htmlFor="docNotes">Observações</label>
            <textarea
              id="docNotes"
              className="dpTextarea"
              maxLength={1000}
              value={form.notes}
              disabled={saving}
              onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
            />
          </div>
        </div>

        <footer className="dpModal__footer">
          <button type="button" className="dpBtn dpBtn--secondary" onClick={onClose} disabled={saving}>
            Cancelar
          </button>
          <button type="button" className="dpBtn dpBtn--primary" onClick={submit} disabled={saving}>
            {progress
              ? `Enviando ${progress.done} de ${progress.total}...`
              : saving
              ? "Salvando..."
              : isBatch
              ? `Enviar ${files.length} documentos`
              : "Salvar"}
          </button>
        </footer>
      </div>
    </div>
  );
}
